import styled from '@emotion/styled'
import { ControlledInput, ExtranetContainer } from '@pharmac-extranet/shared'
import {
  PharmacAccordion,
  PharmacAccordionContent,
  PharmacAccordionHandle,
  PharmacButton,
  PharmacH5,
  PharmacIcon,
  PharmacPortalTooltip,
  PharmacText,
} from '@pharmac/ui'
import { Control, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal'
import { useResetApiKey } from './useResetApiKey'

const AccordionHandle = styled(PharmacAccordionHandle)`
  background-color: var(--color-accent-2);
`

const AccordionContent = styled(PharmacAccordionContent)`
  background-color: var(--color-accent-2);
`

export const ResetApiKeyInputMobile = () => {
  const {
    isAdmin,
    isPharmacy,
    copyToClipboard,
    handleReset,
    showPassword,
    showConfirmationModal,
    setShowConfirmationModal,
    formHook,
  } = useResetApiKey()
  const { t } = useTranslation()
  const { isPharmacCustomerService } = useAuthenticationContext()

  if (!isPharmacy) return null

  return (
    <PharmacAccordion
      marginSmallTop
      handle={({ isOpen }: { isOpen: boolean }) => (
        <AccordionHandle
          variant="secondary"
          isOpen={isOpen}
        >
          <PharmacH5 marginSmallRight>{t('customer.ordersApiKeyTitle')}</PharmacH5>
          <ExtranetContainer onClick={(e) => e.stopPropagation()}>
            <PharmacPortalTooltip
              placement="auto"
              handle={
                <PharmacIcon
                  icon="info"
                  style={{
                    color: 'var(--color-green)',
                    marginRight: 'var(--spacing-tiny)',
                    marginTop: 'var(--spacing-tiny)',
                  }}
                />
              }
            >
              {t('customer.ordersApiKeyTooltip')}
            </PharmacPortalTooltip>
          </ExtranetContainer>
        </AccordionHandle>
      )}
    >
      <AccordionContent variant="secondary">
        <ExtranetContainer
          flexColumn
          full
        >
          <ExtranetContainer full>
            <div
              style={{
                fontFamily: 'var(--font-family-secondary)',
                paddingBottom: 'var(--spacing-large)',
                paddingLeft: 'var(--spacing-tiny)',
                paddingRight: 'var(--spacing-tiny)',
              }}
            >
              {t('customer.ordersApiKeyDisclaimer')}
            </div>
          </ExtranetContainer>
          <ExtranetContainer full>
            <ControlledInput
              name="apiKey"
              readOnly
              control={formHook.control as unknown as Control<FieldValues>}
              type={showPassword ? 'text' : 'password'}
              disabled={!isAdmin || isPharmacCustomerService}
            />
          </ExtranetContainer>
          <ExtranetContainer
            full
            flexColumn
            justifyStart
            marginMediumTop
          >
            <PharmacButton
              style={{ maxWidth: '100%', width: '100%' }}
              disabled={!isAdmin || isPharmacCustomerService}
              marginSmallBottom
              onClick={() => setShowConfirmationModal(true)}
            >
              <PharmacText weight="bold">{t('customer.ordersApiKeyResetButton')}</PharmacText>
              <PharmacIcon
                style={{ marginLeft: 'var(--spacing-small)' }}
                icon="repeat"
                size="16"
              />
            </PharmacButton>
            <PharmacButton
              style={{ maxWidth: '100%', width: '100%' }}
              variant="secondary"
              onClick={copyToClipboard}
              disabled={!showPassword || !isAdmin || isPharmacCustomerService}
            >
              <PharmacText weight="bold">{t('customer.ordersApiKeyCopyToClipboard')}</PharmacText>
              <PharmacIcon
                style={{ marginLeft: 'var(--spacing-small)' }}
                icon="copy"
                size="16"
              />
            </PharmacButton>
          </ExtranetContainer>
        </ExtranetContainer>
      </AccordionContent>
      <ConfirmationModal
        showOnProp={showConfirmationModal}
        title={t('customer.ordersApiKeyModalTitle')}
        onSubmit={handleReset}
        closeButtonText={t('cancel', { ns: 'common' })}
        submitButtonText={t('customer.ordersApiKeyModalOkButton')}
        onClose={() => setShowConfirmationModal(false)}
        content={
          <ExtranetContainer>
            <PharmacText>{t('customer.ordersApiKeyModalDisclaimer')}</PharmacText>
          </ExtranetContainer>
        }
      />
    </PharmacAccordion>
  )
}
